import { logger } from '../../../logger';
import { ElementWithMeta, ModelTarget } from '../../../types';
import { ValueObject } from '../../Smartform';
import { filterNotEmpty } from '../listHelpers';
import { sanitizeFieldName } from './fieldNameSanitizer';
import { objectToPath } from './objectToPath';
import { InputModel } from './types';

/*
    transforms 'bqi' to 'bqi'
    transforms { primaryEngagement: 'customField' } with params { uid: '1' } to 'primaryEngagement.customField.CF_1'
    transforms { primaryEngagement: { person: 'email'} } to 'primaryEngagement.person.email'
 */

export const CUSTOM_FIELD_NAME_PREFIX = 'CF_';

function isCustomField<Type extends string>(type: Type): boolean {
  return type.toLowerCase().includes('customfield');
}

export function getModelPathFromIndex<Model = InputModel>(
  element: ElementWithMeta<Model>,
  index = 0,
  sanitizeCharacters = true
): string {
  const models: ModelTarget<any>[] = filterNotEmpty(element.meta.map((m) => m.model));
  if (models[index]) {
    return getModelPath(
      models[index] as ValueObject | string,
      element.params?.id,
      isCustomField(element.type),
      sanitizeCharacters
    );
  }
  throw new Error('invalid index provided, no such element exists');
}

export function getParentModelPath(fieldPathOrFieldData: string | { name: string }): string {
  const basePath = typeof fieldPathOrFieldData === 'string' ? fieldPathOrFieldData : (fieldPathOrFieldData.name ?? '');
  if (!basePath) {
    logger.warn(
      'No model path found for element',
      typeof fieldPathOrFieldData === 'string' ? '' : JSON.stringify(fieldPathOrFieldData, null, 2)
    );
    return '';
  }
  return basePath.split('.').slice(0, -1).join('.');
}

export function getModelPath(
  model: ValueObject | string,
  customFieldId: string | undefined,
  isCustomField: boolean,
  sanitizeCharacters = true
): string {
  // Can have elements with no metadata model description
  // for example a Link. In those cases, just return with nothing
  if (!objectToPath(model as ValueObject | string)) {
    return '';
  }

  return (
    objectToPath(model as ValueObject | string) +
    (customFieldId && isCustomField
      ? `.${CUSTOM_FIELD_NAME_PREFIX}${sanitizeCharacters ? sanitizeFieldName(customFieldId) : customFieldId}`
      : '')
  );
}

export function getModelPaths<Model = InputModel>(element: ElementWithMeta<Model>): string[] {
  return element.meta.map((_meta, index) => getModelPathFromIndex(element, index));
}
